import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography
} from "@mui/material";
import React, { useState } from "react";

import { Dataset } from "../../../types/dataset";

interface DeleteConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  dataset: Dataset | null;
  loading: boolean;
}

export const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  dataset,
  loading
}) => {
  const [confirmationText, setConfirmationText] = useState("");

  const handleClose = () => {
    if (!loading) {
      setConfirmationText("");
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (confirmationText === dataset?.dataset_name) {
      await onConfirm();
      setConfirmationText("");
    }
  };

  const isConfirmationValid = confirmationText === dataset?.dataset_name;

  if (!dataset) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Delete Dataset</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. All dataset items will be permanently deleted.
          </Alert>

          <Typography variant="body1" gutterBottom>
            You are about to delete the dataset <strong>"{dataset.dataset_name}"</strong>
            which contains <strong>{dataset.dataset_item_count}</strong> items.
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            To confirm deletion, please type the dataset name exactly as shown above:
          </Typography>

          <TextField
            label={`Type "${dataset.dataset_name}" to confirm`}
            fullWidth
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            disabled={loading}
            error={confirmationText.length > 0 && !isConfirmationValid}
            helperText={
              confirmationText.length > 0 && !isConfirmationValid
                ? "Dataset name does not match"
                : ""
            }
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={loading || !isConfirmationValid}
        >
          Delete Dataset
        </Button>
      </DialogActions>
    </Dialog>
  );
};
